import {
  format,
  parse,
  addHours,
  differenceInHours,
  startOfDay,
  setHours,
  setMinutes,
  isAfter,
  isToday as dateFnsIsToday,
} from 'date-fns';

/**
 * Parse time string in HH:mm format to a Date object (using today as base date)
 * @param timeString - Time string in HH:mm format (24-hour)
 * @returns Date object with the time set, or null if invalid
 */
const parseTime = (timeString: string): Date | null => {
  try {
    const referenceDate = startOfDay(new Date());
    const parsed = parse(timeString, 'HH:mm', referenceDate);

    if (Number.isNaN(parsed.getTime())) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
};

/**
 * Helper function to get today's date in user's local timezone (YYYY-MM-DD format)
 */
export const getLocalDateString = (date: Date = new Date()): string => format(date, 'yyyy-MM-dd');

/**
 * Get the default start time based on whether the date is today or a future date.
 * - Future dates: Returns the building-configured default if provided, else '09:00'
 * - Today: Returns next rounded hour if within business hours (8-18), null otherwise.
 *   The building default is intentionally NOT applied when the selected date is today —
 *   a building default like '09:00' is less useful than "next available hour" once that
 *   default time has already passed today.
 *
 * @param date - The selected date
 * @param buildingDefaultStartTime - Optional default start time from building settings
 * @returns Default time in HH:mm format, or null if no default should be applied
 */
export const getDefaultStartTime = (date: Date, buildingDefaultStartTime?: string | null): string | null => {
  if (!dateFnsIsToday(date)) {
    return buildingDefaultStartTime || '09:00';
  }

  const now = new Date();
  const nextHour = now.getHours() + 1;

  if (nextHour > 18 || nextHour < 8) {
    return null;
  }

  return `${nextHour.toString().padStart(2, '0')}:00`;
};

/**
 * Calculate end time from start time and duration (in hours)
 * @param startTime - Start time in HH:mm format
 * @param durationHours - Duration in hours
 * @returns End time in HH:mm format
 */
export const calculateEndTime = (startTime: string, durationHours: number): string => {
  if (!startTime || !durationHours) {
    return startTime;
  }

  const startDate = parseTime(startTime);
  if (!startDate) {
    return startTime;
  }

  const endDate = addHours(startDate, durationHours);
  return format(endDate, 'HH:mm');
};

export const getEffectiveEndTime = (
  startTime: string,
  endTime: string | null | undefined,
  useDuration: boolean | undefined,
  duration: number | undefined,
): string => {
  if (useDuration && duration != null) {
    return calculateEndTime(startTime, duration);
  }
  return endTime || startTime;
};

/**
 * Calculate duration from start and end times (in hours)
 * @param startTime - Start time in HH:mm format
 * @param endTime - End time in HH:mm format
 * @returns Duration in hours (rounded to nearest hour)
 */
export const calculateDuration = (startTime: string, endTime: string): number | null => {
  if (!startTime || !endTime) {
    return null;
  }

  const startDate = parseTime(startTime);
  const endDate = parseTime(endTime);

  if (!startDate || !endDate) {
    return null;
  }

  // Handle case where end time is next day
  let adjustedEndDate = endDate;
  if (endDate < startDate) {
    adjustedEndDate = addHours(endDate, 24);
  }

  const diffHours = differenceInHours(adjustedEndDate, startDate);

  // Round to nearest hour
  return Math.round(diffHours);
};

/**
 * Calculate maximum duration from start time to end of day (23:59)
 * @param startTime - Start time in HH:mm format
 * @returns Maximum duration in hours (rounded down to nearest hour)
 */
export const calculateMaxDuration = (startTime: string): number => {
  if (!startTime) {
    return 8; // Default max if no start time
  }

  const startDate = parseTime(startTime);
  if (!startDate) {
    return 8; // Default max if invalid start time
  }

  const endOfDay = setHours(setMinutes(startOfDay(new Date()), 59), 23);

  // If start time is after end of day, return 0
  if (isAfter(startDate, endOfDay)) {
    return 0;
  }

  const diffHours = differenceInHours(endOfDay, startDate);

  // Return at least 1 hour, or the calculated difference (rounded down)
  return Math.max(1, Math.floor(diffHours));
};
